import React from 'react';
import {
  CustomPolicyDraft,
  MAX_PARTIAL_STEPS,
  describePolicy,
  draftToPolicy,
  validateDraft,
} from 'library/helpers/customCancellationPolicy';

interface Props {
  draft: CustomPolicyDraft;
  onChange: (next: CustomPolicyDraft) => void;
}

const row: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontSize: 14, color: '#1a1a1a' };
const input: React.CSSProperties = {
  width: 64,
  height: 36,
  padding: '0 8px',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  fontSize: 14,
  textAlign: 'center',
  fontFamily: 'inherit',
};
const link: React.CSSProperties = {
  alignSelf: 'flex-start',
  padding: 0,
  border: 0,
  background: 'none',
  color: '#f1662f',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const digits = (v: string): string => v.replace(/\D/g, '').slice(0, 3);

/** Lets the host write their own refund rules: full refund, up to two partial steps, then none. */
export function CustomPolicyEditor({ draft, onChange }: Props) {
  const steps = draft.percents.length;
  const set = (patch: Partial<CustomPolicyDraft>) => onChange({ ...draft, ...patch });
  const problem = validateDraft(draft);
  const policy = draftToPolicy(draft);

  return (
    <div
      style={{
        margin: '10px 0 0 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={row}>
        <span>Full refund if cancelled</span>
        <input
          style={input}
          inputMode="numeric"
          aria-label="Days before check-in for a full refund"
          value={draft.fullDays}
          onChange={(e) => set({ fullDays: digits(e.target.value) })}
        />
        <span>or more days before check-in</span>
      </div>

      {draft.percents.map((pct, i) => {
        const from = i === 0 ? draft.fullDays : draft.midDays;
        const isLast = i === steps - 1;
        return (
          <div key={i} style={row}>
            <input
              style={input}
              inputMode="numeric"
              aria-label={`Refund percentage for step ${i + 1}`}
              value={pct}
              onChange={(e) => {
                const next = [...draft.percents];
                next[i] = digits(e.target.value);
                set({ percents: next });
              }}
            />
            <span>% refund if cancelled between {from || '…'} and</span>
            <input
              style={input}
              inputMode="numeric"
              aria-label={`Days where refund step ${i + 1} ends`}
              value={isLast ? draft.noRefundDays : draft.midDays}
              onChange={(e) =>
                set(isLast ? { noRefundDays: digits(e.target.value) } : { midDays: digits(e.target.value) })
              }
            />
            <span>days before check-in</span>
          </div>
        );
      })}

      {steps > 0 ? (
        <div style={{ ...row, color: '#6b7280' }}>
          <span>No refund if cancelled less than {draft.noRefundDays || '…'} days before check-in.</span>
        </div>
      ) : (
        <div style={{ ...row, color: '#6b7280' }}>
          <span>No refund if cancelled less than {draft.fullDays || '…'} days before check-in.</span>
        </div>
      )}

      {steps < MAX_PARTIAL_STEPS ? (
        <button
          type="button"
          style={link}
          onClick={() => set({ percents: [...draft.percents, ''] })}
        >
          + Add a partial refund step
        </button>
      ) : null}
      {steps > 0 ? (
        <button
          type="button"
          style={{ ...link, color: '#6b7280' }}
          onClick={() => set({ percents: draft.percents.slice(0, -1), midDays: steps === 2 ? '' : draft.midDays })}
        >
          Remove last step
        </button>
      ) : null}

      {problem && draft.fullDays !== '' ? (
        <p role="alert" style={{ margin: 0, fontSize: 13, color: '#b45309' }}>
          {problem}
        </p>
      ) : null}

      {policy ? (
        <div style={{ padding: 12, borderRadius: 10, background: 'rgba(241, 102, 47, 0.06)' }}>
          <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 700, color: '#6b7280' }}>What guests will see</p>
          {describePolicy(policy).map((line) => (
            <p key={line} style={{ margin: '2px 0', fontSize: 13, color: '#1a1a1a' }}>
              {line}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}
